import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  Shield, Plus, Play, Clock, CheckCircle, XCircle, Calendar, Sparkles, 
  KeyRound, AlertTriangle, Loader2, Globe, Server, Smartphone, Network,
  FileCheck, ExternalLink, ChevronRight, Search, Filter, RefreshCw, Upload, Code,
  ListChecks, Activity, CalendarClock, MoreVertical, Pause, Eye, StopCircle, Square, Trash2, CheckSquare, Lock, Package
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AssessmentCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningProgress, setRunningProgress] = useState({});
  const progressIntervalRef = useRef({});
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeFile, setCodeFile] = useState(null);
  const [uploadingCode, setUploadingCode] = useState(false);
  const [codeScans, setCodeScans] = useState([]);
  const codeFileRef = useRef(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const mobileFileRef = useRef(null);
  const [selectedAssessments, setSelectedAssessments] = useState(new Set());
  
  // Enterprise Scanning & Pentest State
  const [scanTypes, setScanTypes] = useState([]);
  const [pentestModes, setPentestModes] = useState([]);
  const [enterpriseScanTarget, setEnterpriseScanTarget] = useState('');
  const [selectedScanType, setSelectedScanType] = useState('full');
  const [pentestTarget, setPentestTarget] = useState('');
  const [selectedPentestMode, setSelectedPentestMode] = useState('safe_simulation');
  const [runningEnterpriseScan, setRunningEnterpriseScan] = useState(false);
  const [runningPentest, setRunningPentest] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [pentestResults, setPentestResults] = useState([]);
  const [showPentestConfirmation, setShowPentestConfirmation] = useState(false);
  
  // Authentication State for Enterprise Scanner
  const [scanAuthEnabled, setScanAuthEnabled] = useState(false);
  const [scanAuthType, setScanAuthType] = useState('basic');
  const [scanAuthCredentials, setScanAuthCredentials] = useState({
    username: '',
    password: '',
    token: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_token_url: ''
  });
  
  // Authentication State for AI Pentest
  const [pentestAuthEnabled, setPentestAuthEnabled] = useState(false);
  const [pentestAuthType, setPentestAuthType] = useState('basic');
  const [pentestAuthCredentials, setPentestAuthCredentials] = useState({
    username: '',
    password: '',
    token: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_token_url: ''
  });
  
  // Get active tab from URL or default to 'schedule'
  const activeTab = searchParams.get('tab') || 'sbom';
  
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };
  
  const [newAssessment, setNewAssessment] = useState({
    name: '',
    assessment_type: 'web',
    target: '',
    schedule: '',
    scheduled_at: '',
    is_ai_pentest: false,
    pentest_type: 'ai_powered',
    compliance_domain: '',
    is_authenticated: false,
    auth_type: 'basic',
    auth_username: '',
    auth_password: '',
    auth_token: '',
    auth_login_url: '',
  });

  useEffect(() => {
    fetchAssessments();
    fetchEnterpriseData();
    return () => {
      Object.values(progressIntervalRef.current).forEach(clearInterval);
    };
  }, []);

  const fetchEnterpriseData = async () => {
    try {
      const [scanTypesRes, pentestModesRes, scanHistoryRes, pentestHistoryRes] = await Promise.all([
        api.get('/scanner/types').catch(() => ({ data: { scan_types: [] } })),
        api.get('/pentest/modes').catch(() => ({ data: { modes: [] } })),
        api.get('/scanner/history?limit=10').catch(() => ({ data: [] })),
        api.get('/pentest/history?limit=10').catch(() => ({ data: [] }))
      ]);
      
      setScanTypes(scanTypesRes.data.scan_types || []);
      setPentestModes(pentestModesRes.data.modes || []);
      setScanResults(scanHistoryRes.data || []);
      setPentestResults(pentestHistoryRes.data || []);
    } catch (error) {
      console.error('Failed to fetch enterprise data:', error);
    }
  };

  const handleRunEnterpriseScan = async () => {
    if (!enterpriseScanTarget) {
      toast.error('Please enter a target URL or IP');
      return;
    }
    
    setRunningEnterpriseScan(true);
    try {
      const payload = {
        target: enterpriseScanTarget,
        scan_type: selectedScanType
      };
      
      // Add authentication if enabled
      if (scanAuthEnabled) {
        payload.authentication = {
          enabled: true,
          type: scanAuthType,
          credentials: scanAuthType === 'basic' ? {
            username: scanAuthCredentials.username,
            password: scanAuthCredentials.password
          } : scanAuthType === 'bearer' ? {
            token: scanAuthCredentials.token
          } : {
            client_id: scanAuthCredentials.oauth_client_id,
            client_secret: scanAuthCredentials.oauth_client_secret,
            token_url: scanAuthCredentials.oauth_token_url
          }
        };
      }
      
      const response = await api.post('/scanner/run', payload);
      
      toast.success(`Scan completed: ${response.data.findings_count} findings discovered`);
      fetchEnterpriseData();
      setEnterpriseScanTarget('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    } finally {
      setRunningEnterpriseScan(false);
    }
  };

  const handleRunPentest = async () => {
    if (!pentestTarget) {
      toast.error('Please enter a target URL');
      return;
    }
    
    // Require confirmation for sandboxed exploitation
    if (selectedPentestMode === 'sandboxed_exploitation' && !showPentestConfirmation) {
      setShowPentestConfirmation(true);
      return;
    }
    
    setRunningPentest(true);
    setShowPentestConfirmation(false);
    try {
      const payload = {
        target: pentestTarget,
        mode: selectedPentestMode
      };
      
      // Add authentication if enabled
      if (pentestAuthEnabled) {
        payload.authentication = {
          enabled: true,
          type: pentestAuthType,
          credentials: pentestAuthType === 'basic' ? {
            username: pentestAuthCredentials.username,
            password: pentestAuthCredentials.password
          } : pentestAuthType === 'bearer' ? {
            token: pentestAuthCredentials.token
          } : {
            client_id: pentestAuthCredentials.oauth_client_id,
            client_secret: pentestAuthCredentials.oauth_client_secret,
            token_url: pentestAuthCredentials.oauth_token_url
          }
        };
      }
      
      const response = await api.post('/pentest/run', payload);
      
      toast.success(`Penetration test completed. Risk Score: ${response.data.overall_risk_score?.toFixed(1)}`);
      fetchEnterpriseData();
      setPentestTarget('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Penetration test failed');
    } finally {
      setRunningPentest(false);
    }
  };

  useEffect(() => {
    const runningAssessments = assessments.filter(a => a.status === 'running');
    runningAssessments.forEach(assessment => {
      if (!progressIntervalRef.current[assessment.id]) {
        pollProgress(assessment.id);
        progressIntervalRef.current[assessment.id] = setInterval(() => {
          pollProgress(assessment.id);
        }, 2000);
      }
    });

    Object.keys(progressIntervalRef.current).forEach(id => {
      const assessment = assessments.find(a => a.id === id);
      if (!assessment || assessment.status !== 'running') {
        clearInterval(progressIntervalRef.current[id]);
        delete progressIntervalRef.current[id];
      }
    });
  }, [assessments]);

  const pollProgress = async (assessmentId) => {
    try {
      const response = await api.get(`/assessments/${assessmentId}/progress`);
      setRunningProgress(prev => ({
        ...prev,
        [assessmentId]: response.data
      }));

      if (response.data.status === 'completed') {
        clearInterval(progressIntervalRef.current[assessmentId]);
        delete progressIntervalRef.current[assessmentId];
        fetchAssessments();
        toast.success('Assessment completed successfully');
      }
    } catch (error) {
      console.error('Failed to poll progress:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await api.get('/assessments');
      setAssessments(response.data);
    } catch (error) {
      toast.error('Failed to fetch assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCodeScans = async () => {
    try {
      const response = await api.get('/secure-code/scans');
      setCodeScans(response.data);
    } catch (error) {
      console.error('Failed to fetch code scans');
    }
  };

  useEffect(() => {
    fetchCodeScans();
  }, []);

  const handleCodeUpload = async () => {
    if (!codeFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploadingCode(true);
    try {
      const formData = new FormData();
      formData.append('file', codeFile);
      
      const response = await api.post('/secure-code/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Code scan complete: ${response.data.vulnerabilities_found} vulnerabilities found`);
      setCodeFile(null);
      if (codeFileRef.current) codeFileRef.current.value = '';
      fetchCodeScans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code scan failed');
    } finally {
      setUploadingCode(false);
    }
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    // Target is optional for mobile assessments (file upload based)
    const targetRequired = newAssessment.assessment_type !== 'mobile';
    if (!newAssessment.name || (targetRequired && !newAssessment.target)) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      // For mobile assessments, use file name as target if no target provided
      const payload = {
        ...newAssessment,
        target: newAssessment.target || (mobileFile ? mobileFile.name : 'Mobile App Assessment')
      };
      await api.post('/assessments', payload);
      toast.success('Assessment scheduled successfully');
      setNewAssessment({
        name: '',
        assessment_type: 'web',
        target: '',
        schedule: '',
        scheduled_at: '',
        is_ai_pentest: false,
        pentest_type: 'ai_powered',
        compliance_domain: '',
        is_authenticated: false,
        auth_type: 'basic',
        auth_username: '',
        auth_password: '',
        auth_token: '',
        auth_login_url: '',
      });
      setMobileFile(null);
      fetchAssessments();
      // Navigate to pending tab after scheduling
      setActiveTab('pending');
    } catch (error) {
      toast.error('Failed to create assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartAssessment = async (assessmentId) => {
    try {
      setAssessments(prev => prev.map(a => 
        a.id === assessmentId ? { ...a, status: 'running' } : a
      ));
      setRunningProgress(prev => ({
        ...prev,
        [assessmentId]: { progress: 5, current_step: 'Initializing scan', status: 'running' }
      }));

      const response = await api.post(`/assessments/${assessmentId}/start`);
      toast.success('Assessment started');
      if (response.data.suggestions) {
        toast.info('AI assessment plan generated');
      }
      fetchAssessments();
      // Switch to In-Progress tab
      setActiveTab('inprogress');
    } catch (error) {
      toast.error('Failed to start assessment');
      fetchAssessments();
    }
  };

  const handleStopAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to stop this running assessment? Progress will be lost.')) {
      return;
    }
    
    try {
      await api.post(`/assessments/${assessmentId}/stop`);
      toast.success('Assessment stopped');
      
      // Clear progress tracking
      if (progressIntervalRef.current[assessmentId]) {
        clearInterval(progressIntervalRef.current[assessmentId]);
        delete progressIntervalRef.current[assessmentId];
      }
      setRunningProgress(prev => {
        const updated = { ...prev };
        delete updated[assessmentId];
        return updated;
      });
      
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stop assessment');
    }
  };

  const handleRetestAssessment = async (assessment) => {
    try {
      const retestData = {
        name: `${assessment.name} (Retest)`,
        assessment_type: assessment.assessment_type,
        target: assessment.target,
        schedule: 'none',
        scheduled_at: '',
        is_ai_pentest: assessment.is_ai_pentest || false,
        pentest_type: assessment.pentest_type || 'ai_powered',
        is_authenticated: assessment.is_authenticated || false,
        auth_type: assessment.auth_type || 'basic',
        auth_username: assessment.auth_username || '',
        auth_password: assessment.auth_password || '',
        auth_token: assessment.auth_token || '',
        auth_login_url: assessment.auth_login_url || '',
      };
      
      const response = await api.post('/assessments', retestData);
      toast.success('Retest assessment created');
      
      if (response.data?.id) {
        await handleStartAssessment(response.data.id);
      }
      fetchAssessments();
    } catch (error) {
      toast.error('Failed to create retest assessment');
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) {
      return;
    }
    
    try {
      await api.delete(`/assessments/${assessmentId}`);
      toast.success('Assessment deleted');
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      web: <Globe className="w-4 h-4" />,
      api: <Server className="w-4 h-4" />,
      mobile: <Smartphone className="w-4 h-4" />,
      infrastructure: <Network className="w-4 h-4" />,
      network: <Network className="w-4 h-4" />,
      compliance: <FileCheck className="w-4 h-4" />,
      secure_code: <KeyRound className="w-4 h-4" />,
    };
    return icons[type] || <Shield className="w-4 h-4" />;
  };

  // Bulk operations
  const toggleSelectAllAssessments = (assessmentList) => {
    const allIds = new Set(assessmentList.map(a => a.id));
    const allSelected = assessmentList.every(a => selectedAssessments.has(a.id));
    if (allSelected) {
      setSelectedAssessments(prev => {
        const newSet = new Set(prev);
        allIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      setSelectedAssessments(prev => new Set([...prev, ...allIds]));
    }
  };

  const toggleAssessmentSelection = (id) => {
    setSelectedAssessments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedAssessments.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedAssessments.size} assessment(s)?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssessments) {
      try {
        await api.delete(`/assessments/${id}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} assessment(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} assessment(s)`);
    }
    
    setSelectedAssessments(new Set());
    fetchAssessments();
  };

  const handleBulkStart = async () => {
    if (selectedAssessments.size === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssessments) {
      try {
        await api.post(`/assessments/${id}/start`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Started ${successCount} assessment(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to start ${failCount} assessment(s)`);
    }
    
    setSelectedAssessments(new Set());
    fetchAssessments();
  };

  // Filter assessments based on type and search
  const filteredAssessments = assessments.filter(a => {
    const matchesType = typeFilter === 'all' || a.assessment_type === typeFilter;
    const matchesSearch = searchQuery === '' || 
      a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.target.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Categorize assessments by status
  const pendingAssessments = filteredAssessments.filter(a => a.status === 'pending' || a.status === 'scheduled');
  const runningAssessments = filteredAssessments.filter(a => a.status === 'running');
  const completedAssessments = filteredAssessments.filter(a => a.status === 'completed');

  // Stats for header badges
  const stats = {
    pending: pendingAssessments.length,
    inprogress: runningAssessments.length,
    completed: completedAssessments.length,
    total: assessments.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className={`font-semibold text-lg ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
          Loading assessments...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="assessment-center-container" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDarkMode 
              ? 'bg-indigo-500/15 border border-indigo-500/30' 
              : 'bg-indigo-100 border border-indigo-200'
          }`}>
            <Activity className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl lg:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Assessment Center
            </h1>
            <p className={`text-sm font-inter mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Schedule, manage, and track security assessments
            </p>
          </div>
        </div>
        
        {/* Stats Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
            <Clock className={`w-4 h-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <span className={`text-sm font-inter font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{stats.pending} Pending</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-electric-500/10 border border-electric-500/30' : 'bg-electric-50 border border-electric-200'}`}>
            <Activity className={`w-4 h-4 ${isDarkMode ? 'text-electric-400' : 'text-electric-600'}`} />
            <span className={`text-sm font-inter font-medium ${isDarkMode ? 'text-electric-400' : 'text-electric-700'}`}>{stats.inprogress} Running</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
            <CheckCircle className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-sm font-inter font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{stats.completed} Completed</span>
          </div>
        </div>
      </div>

      {/* Main Tabs - TVM Platform Mirror */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className={`w-full max-w-xs min-w-max ${isDarkMode ? 'bg-navy-800/50 border border-navy-700' : 'bg-slate-100 border border-slate-200'}`}>
            <TabsTrigger 
              value="sbom" 
              data-testid="tab-sbom"
              className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${isDarkMode ? 'data-[state=active]:bg-indigo-500 data-[state=active]:text-white' : ''}`}
            >
              <Shield className="w-3 sm:w-4 h-3 sm:h-4" />
              <span className="sm:inline">SBOM Management</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* SBOM Management Tab */}
        <TabsContent value="sbom" className="space-y-4">
          <SBOMPanel isDarkMode={isDarkMode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
// Reusable Assessment Table Component
function AssessmentTable({ title, description, data, status, isDarkMode, getTypeIcon, onStart, onStop, onRetest, onDelete, runningProgress, navigate, showVulnLink = false, selectedItems, onToggleSelect }) {
  const getStatusColor = (s) => {
    switch (s) {
      case 'pending': return isDarkMode ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-600 bg-yellow-50';
      case 'scheduled': return isDarkMode ? 'text-orange-400 bg-orange-500/10' : 'text-orange-600 bg-orange-50';
      case 'running': return isDarkMode ? 'text-blue-400 bg-blue-500/10' : 'text-blue-600 bg-blue-50';
      case 'completed': return isDarkMode ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-600 bg-emerald-50';
      default: return isDarkMode ? 'text-zinc-400 bg-zinc-500/10' : 'text-slate-600 bg-slate-50';
    }
  };

  return (
    <div className={`rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'} overflow-hidden`}>
      <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{description}</p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
            status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
            status === 'running' ? 'bg-blue-500/20 text-blue-500' :
            status === 'completed' ? 'bg-emerald-500/20 text-emerald-500' :
            'bg-zinc-500/20 text-zinc-500'
          }`}>
            {data.length} {data.length === 1 ? 'assessment' : 'assessments'}
          </span>
        </div>
      </div>
      
      {data.length === 0 ? (
        <div className={`p-12 text-center ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">No {status} assessments</p>
          <p className="text-xs mt-1 opacity-70">
            {status === 'pending' ? 'Schedule a new assessment to get started' : 
             status === 'running' ? 'Start an assessment to see progress' :
             'Complete assessments will appear here'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className={isDarkMode ? 'border-white/10' : 'border-slate-200'}>
                {selectedItems && onToggleSelect && (
                  <TableHead className="w-10"></TableHead>
                )}
                <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Assessment</TableHead>
                <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Type</TableHead>
                <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Target</TableHead>
                {status === 'running' && (
                  <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Progress</TableHead>
                )}
                {showVulnLink && (
                  <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Findings</TableHead>
                )}
                <TableHead className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Date</TableHead>
                <TableHead className={`text-xs uppercase tracking-wider text-right ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((assessment) => (
                <TableRow 
                  key={assessment.id} 
                  data-testid={`assessment-row-${assessment.id}`}
                  className={`${isDarkMode ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'}`}
                >
                  {selectedItems && onToggleSelect && (
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.has(assessment.id)}
                        onCheckedChange={() => onToggleSelect(assessment.id)}
                        data-testid={`select-assessment-${assessment.id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {assessment.is_ai_pentest && (
                        <Sparkles className="w-3 h-3 text-violet-400" />
                      )}
                      <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {assessment.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                      isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {getTypeIcon(assessment.assessment_type)}
                      <span className="capitalize">{assessment.assessment_type}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className={`text-xs font-mono ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      {assessment.target.length > 35 ? assessment.target.substring(0, 35) + '...' : assessment.target}
                    </code>
                  </TableCell>
                  {status === 'running' && runningProgress[assessment.id] && (
                    <TableCell>
                      <div className="w-36 space-y-1">
                        <Progress value={runningProgress[assessment.id].progress || 0} className="h-2" />
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          {runningProgress[assessment.id].progress}% - {runningProgress[assessment.id].current_step}
                        </p>
                      </div>
                    </TableCell>
                  )}
                  {showVulnLink && (
                    <TableCell>
                      {assessment.vulnerabilities_found > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/vulnerability-hub?assessment=${assessment.id}`)}
                          className={`text-xs ${isDarkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-500'}`}
                          data-testid={`view-vulns-${assessment.id}`}
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {assessment.vulnerabilities_found} findings
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      ) : (
                        <span className={`text-xs flex items-center ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Clean
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                    {new Date(assessment.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {(status === 'pending' || assessment.status === 'scheduled') && (
                      <div className="flex items-center gap-2 justify-end">
                        {onStart && (
                          <Button
                            size="sm"
                            onClick={() => onStart(assessment.id)}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                            data-testid={`start-assessment-${assessment.id}`}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Start
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onDelete(assessment.id)}
                            className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            data-testid={`delete-assessment-${assessment.id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                    {status === 'running' && (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Running
                        </span>
                        {onStop && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onStop(assessment.id)}
                            className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            data-testid={`stop-assessment-${assessment.id}`}
                          >
                            <Square className="w-3 h-3 mr-1 fill-current" />
                            Stop
                          </Button>
                        )}
                      </div>
                    )}
                    {status === 'completed' && onRetest && (
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/assessment/${assessment.id}`)}
                          className={`text-xs ${isDarkMode ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                          data-testid={`view-assessment-${assessment.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRetest(assessment)}
                          className={`text-xs ${isDarkMode ? 'border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-50'}`}
                          data-testid={`retest-assessment-${assessment.id}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Retest
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


// SBOM Panel Component
function SBOMPanel({ isDarkMode }) {
  const [sboms, setSboms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    format: 'cyclonedx',
    version: '',
    sbomJson: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSBOMs();
  }, []);

  const fetchSBOMs = async () => {
    try {
      const response = await api.get('/sbom/inventory');
      setSboms(response.data.sboms || []);
    } catch (error) {
      console.error('Failed to fetch SBOMs:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadSBOM = async () => {
    if (!uploadData.name || !uploadData.sbomJson) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      const sbomData = JSON.parse(uploadData.sbomJson);
      const response = await api.post('/sbom/upload', {
        name: uploadData.name,
        format: uploadData.format,
        version: uploadData.version,
        sbom_data: sbomData
      });
      
      toast.success(`SBOM uploaded: ${response.data.vulnerabilities_found} vulnerabilities found`);
      setShowUploadModal(false);
      setUploadData({ name: '', format: 'cyclonedx', version: '', sbomJson: '' });
      fetchSBOMs();
    } catch (error) {
      toast.error('Failed to upload SBOM: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const rescanSBOM = async (sbomId) => {
    try {
      const response = await api.post(`/sbom/${sbomId}/rescan`);
      toast.success(`Rescan complete: ${response.data.vulnerabilities_found} vulnerabilities found`);
      fetchSBOMs();
    } catch (error) {
      toast.error('Failed to rescan SBOM');
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'text-red-400';
    if (score >= 40) return 'text-orange-400';
    if (score >= 20) return 'text-yellow-400';
    return 'text-emerald-400';
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  // Calculate stats
  const stats = {
    totalSboms: sboms.length,
    totalComponents: sboms.reduce((sum, s) => sum + (s.total_components || 0), 0),
    totalVulns: sboms.reduce((sum, s) => sum + (s.vulnerabilities_count || 0), 0),
    highestRisk: sboms.reduce((max, s) => Math.max(max, s.risk_score || 0), 0)
  };

  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-inter text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <Shield className="w-5 h-5 text-indigo-400" />
            Software Bill of Materials (SBOM)
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Supply-chain security scanning for NIST EO 14028 compliance
          </p>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Upload className="w-4 h-4 mr-2" /> Upload SBOM
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'}`}>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>{stats.totalSboms}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total SBOMs</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{stats.totalComponents}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Components</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-200'}`}>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>{stats.totalVulns}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Vulnerabilities</p>
        </div>
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
          <p className={`text-2xl font-bold ${getRiskColor(stats.highestRisk)}`}>{stats.highestRisk}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Highest Risk</p>
        </div>
      </div>

      {sboms.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No SBOMs uploaded yet</p>
          <p className="text-sm mt-1">Upload a CycloneDX or SPDX formatted SBOM</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sboms.map((sbom) => (
            <div key={sbom.id} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                    <Shield className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{sbom.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs uppercase ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                        {sbom.format}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">NIST EO Compliant</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {sbom.total_components || 0} components
                      </span>
                      <span className={`text-sm ${sbom.vulnerabilities_count > 0 ? 'text-orange-400' : isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {sbom.vulnerabilities_count || 0} vulnerabilities
                      </span>
                      <span className={`text-sm ${getRiskColor(sbom.risk_score || 0)}`}>
                        Risk Score: {sbom.risk_score || 0}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                      Uploaded: {new Date(sbom.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => rescanSBOM(sbom.id)}
                  className={isDarkMode ? 'border-white/10' : ''}
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Rescan
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg p-6 rounded-2xl ${isDarkMode ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-slate-200'}`}>
            <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Upload SBOM</h3>
            <div className="space-y-4">
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>SBOM Name *</Label>
                <Input
                  value={uploadData.name}
                  onChange={(e) => setUploadData({...uploadData, name: e.target.value})}
                  placeholder="MyApp Frontend v2.1.0"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>Format</Label>
                  <Select value={uploadData.format} onValueChange={(v) => setUploadData({...uploadData, format: v})}>
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                      <SelectItem value="cyclonedx">CycloneDX</SelectItem>
                      <SelectItem value="spdx">SPDX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>Version</Label>
                  <Input
                    value={uploadData.version}
                    onChange={(e) => setUploadData({...uploadData, version: e.target.value})}
                    placeholder="2.1.0"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10 text-white' : ''}`}
                  />
                </div>
              </div>
              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-600'}>SBOM JSON *</Label>
                <textarea
                  value={uploadData.sbomJson}
                  onChange={(e) => setUploadData({...uploadData, sbomJson: e.target.value})}
                  placeholder='{"bomFormat": "CycloneDX", "specVersion": "1.4", "components": [...]}'
                  rows={8}
                  className={`w-full mt-1 p-3 rounded-lg font-mono text-xs ${isDarkMode ? 'bg-zinc-800 border border-white/10 text-white' : 'bg-slate-50 border border-slate-300'}`}
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowUploadModal(false)} className={isDarkMode ? 'border-white/10' : ''}>
                  Cancel
                </Button>
                <Button onClick={uploadSBOM} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-700">
                  {uploading ? 'Uploading...' : 'Upload SBOM'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
